import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from 'react-select';


// Format numbers to Indian locale
const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const selectCustomStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#020617',
    borderColor: state.isFocused ? '#6366f1' : '#334155',
    padding: '2px',
    borderRadius: '0.75rem',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    '&:hover': { borderColor: '#475569' }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    zIndex: 50
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#1e293b' : 'transparent',
    color: state.isSelected ? 'white' : '#e2e8f0',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#4338ca' }
  }),
  singleValue: (base) => ({ ...base, color: 'white' }),
  input: (base) => ({ ...base, color: 'white' }),
  placeholder: (base) => ({ ...base, color: '#94a3b8' })
};

const WorkingCapitalMaster = ({ isEmbedded, selectedReportId }) => {
  const [reportsList, setReportsList] = useState([]);
  const [reportId, setReportId] = useState('');
  const [segments, setSegments] = useState([]);
  const [globalMarginPerc, setGlobalMarginPerc] = useState('25');

  const handleGlobalMarginChange = (val) => {
    setGlobalMarginPerc(val);
    const updated = segments.map(s => {
      if (s.isNew) {
        return { ...s, margin_percentage: val };
      }
      return s;
    });
    setSegments(updated);
  };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, id: null });
  const tableContainerRef = useRef(null);

  // Fetch all reports for the dropdown
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true })
      .then(res => setReportsList(res.data))
      .catch(() => toast.error('Failed to load reports'));
  }, []);

  useEffect(() => {
    if (isEmbedded && selectedReportId) {
      setReportId(selectedReportId);
    }
  }, [isEmbedded, selectedReportId]);

  // Fetch Working Capital data when report is selected
  useEffect(() => {
    if (!reportId) {
      setSegments([]);
      setGlobalMarginPerc('25');
      return;
    }
    
    // Set global margin from report master data
    const currentReport = reportsList.find(r => String(r.id) === String(reportId));
    let defaultMargin = '25';
    if (currentReport && currentReport.margin_percentage != null) {
      defaultMargin = String(currentReport.margin_percentage).replace(/\.00$/, '');
    }
    setGlobalMarginPerc(defaultMargin);

    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/api/working-capital/${reportId}`, { withCredentials: true })
      .then(res => {
        if (res.data.length > 0) {
          const formattedData = res.data.map(item => ({
            ...item,
            stock_requirement: item.stock_requirement ? String(item.stock_requirement).replace(/\.00$/, '') : item.stock_requirement,
            credit_available: item.credit_available ? String(item.credit_available).replace(/\.00$/, '') : item.credit_available,
            margin_percentage: item.margin_percentage ? String(item.margin_percentage).replace(/\.00$/, '') : item.margin_percentage
          }));
          setSegments(formattedData);
        } else {
          // Add one empty row by default if no data exists
          setSegments([{
            id: 'new_' + Date.now(),
            segment_name: '',
            stock_requirement: 0,
            credit_period: 0,
            credit_available: 0,
            margin_percentage: defaultMargin,
            isNew: true
          }]);
        }
      })
      .catch(() => toast.error('Failed to fetch working capital data'))
      .finally(() => setLoading(false));
  }, [reportId, reportsList]);

  const handleAddRow = () => {
    setSegments([
      ...segments,
      {
        id: 'new_' + Date.now(),
        segment_name: '',
        stock_requirement: '',
        credit_period: '',
        credit_available: '',
        margin_percentage: globalMarginPerc,
        isNew: true
      }
    ]);

    // Auto-scroll to the bottom of the table smoothly
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTo({
          top: tableContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleRemoveRow = (index, id) => {
    const isNew = String(id).startsWith('new_');
    if (!isNew) {
      setDeleteModal({ isOpen: true, index, id });
    } else {
      const updated = [...segments];
      updated.splice(index, 1);
      setSegments(updated);
    }
  };

  const confirmDelete = async () => {
    const { index, id } = deleteModal;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/working-capital/${id}`, { withCredentials: true });
      toast.success('Segment deleted successfully');
      const updated = [...segments];
      updated.splice(index, 1);
      setSegments(updated);
    } catch (err) {
      toast.error('Failed to delete segment');
    } finally {
      setDeleteModal({ isOpen: false, index: null, id: null });
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...segments];
    updated[index][field] = value;
    setSegments(updated);
  };

  const handleSaveAll = async () => {
    if (!reportId) return toast.error('Select a report first');
    setSaving(true);
    try {
      for (const segment of segments) {
        // Calculate derived values for backend saving (optional since we have frontend logic, but good for DB)
        const netPaid = (parseFloat(segment.stock_requirement) || 0) - (parseFloat(segment.credit_available) || 0);
        const marginPerc = segment.margin_percentage !== undefined && segment.margin_percentage !== '' ? parseFloat(segment.margin_percentage) : (parseFloat(globalMarginPerc) || 0);
        const marginAmt = netPaid * (marginPerc / 100);
        const finance = netPaid - marginAmt;

        const payload = {
          report_id: reportId,
          segment_name: segment.segment_name,
          stock_requirement: segment.stock_requirement,
          credit_period: segment.credit_period,
          credit_available: segment.credit_available,
          net_paid_stock: netPaid,
          margin_percentage: marginPerc,
          margin_amount: marginAmt,
          working_capital_finance: finance
        };

        if (String(segment.id).startsWith('new_')) {
          if (!segment.segment_name.trim()) continue; // Skip empty rows
          await axios.post(`${process.env.REACT_APP_API_URL}/api/working-capital`, payload, { withCredentials: true });
        } else {
          await axios.put(`${process.env.REACT_APP_API_URL}/api/working-capital/${segment.id}`, payload, { withCredentials: true });
        }
      }
      toast.success('Saved successfully!');
      // Reload data
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/working-capital/${reportId}`, { withCredentials: true });
      setSegments(res.data);
    } catch (err) {
      toast.error('Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  // Grand totals
  const totalStockReq = segments.reduce((sum, s) => sum + (parseFloat(s.stock_requirement) || 0), 0);
  const totalCreditAvail = segments.reduce((sum, s) => sum + (parseFloat(s.credit_available) || 0), 0);
  const totalNetPaid = segments.reduce((sum, s) => sum + ((parseFloat(s.stock_requirement) || 0) - (parseFloat(s.credit_available) || 0)), 0);
  const totalMarginAmt = segments.reduce((sum, s) => {
    const netPaid = (parseFloat(s.stock_requirement) || 0) - (parseFloat(s.credit_available) || 0);
    const marginPerc = s.margin_percentage !== undefined && s.margin_percentage !== '' ? parseFloat(s.margin_percentage) : (parseFloat(globalMarginPerc) || 0);
    return sum + (netPaid * (marginPerc / 100));
  }, 0);
  const totalFinance = segments.reduce((sum, s) => {
    const netPaid = (parseFloat(s.stock_requirement) || 0) - (parseFloat(s.credit_available) || 0);
    const marginPerc = s.margin_percentage !== undefined && s.margin_percentage !== '' ? parseFloat(s.margin_percentage) : (parseFloat(globalMarginPerc) || 0);
    const margin = netPaid * (marginPerc / 100);
    return sum + (netPaid - margin);
  }, 0);

  const handleExportExcel = () => {
    if (!segments.length) {
      toast.error('No data to export!');
      return;
    }

    const cell = (v, s) => ({ v, s, t: typeof v === 'number' ? 'n' : 's' });
    const styles = {
      title: { font: { bold: true, sz: 14 } },
      header: { font: { bold: true, color: { rgb: 'FFFFFFFF' } }, fill: { fgColor: { rgb: 'FF4338CA' } }, alignment: { horizontal: 'center' } },
      text: { font: { color: { rgb: 'FF1E293B' } } },
      number: { font: { color: { rgb: 'FF1E293B' } }, alignment: { horizontal: 'right' } },
      totalRow: { font: { bold: true, color: { rgb: 'FF1E293B' } }, fill: { fgColor: { rgb: 'FFDCEDC8' } }, alignment: { horizontal: 'right' } }
    };

    const wsData = [
      [cell('SEGMENT-WISE WORKING CAPITAL PROJECTION', styles.title)],
      [],
      [
        cell('Sl.', styles.header),
        cell('Segment Name', styles.header),
        cell('Stock Req (Rs)', styles.header),
        cell('Credit Days', styles.header),
        cell('Credit Avail (Rs)', styles.header),
        cell('Net Paid (Rs)', styles.header),
        cell('Margin %', styles.header),
        cell('Margin (Rs)', styles.header),
        cell('WC Finance (Rs)', styles.header)
      ]
    ];

    segments.forEach((s, i) => {
      const stockReq = parseFloat(s.stock_requirement) || 0;
      const creditPeriod = parseFloat(s.credit_period) || 0;
      const creditAvail = parseFloat(s.credit_available) || 0;
      const netPaid = stockReq - creditAvail;
      const marginPct = s.margin_percentage !== undefined && s.margin_percentage !== '' ? parseFloat(s.margin_percentage) : (parseFloat(globalMarginPerc) || 0);
      const marginAmt = netPaid * (marginPct / 100);
      const wcFinance = netPaid - marginAmt;

      wsData.push([
        cell(i + 1, styles.text),
        cell(s.segment_name || '', styles.text),
        cell(stockReq, styles.number),
        cell(creditPeriod, styles.number),
        cell(creditAvail, styles.number),
        cell(netPaid, styles.number),
        cell(marginPct, styles.number),
        cell(marginAmt, styles.number),
        cell(wcFinance, styles.number)
      ]);
    });

    wsData.push([
      cell('', styles.totalRow),
      cell('TOTAL', styles.totalRow),
      cell(totalStockReq, styles.totalRow),
      cell('', styles.totalRow),
      cell(totalCreditAvail, styles.totalRow),
      cell(totalNetPaid, styles.totalRow),
      cell('', styles.totalRow),
      cell(totalMarginAmt, styles.totalRow),
      cell(totalFinance, styles.totalRow)
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    worksheet['!cols'] = [
      { wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, 
      { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 20 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Working Capital');
    XLSX.writeFile(workbook, 'Working_Capital_Computations.xlsx');
  };

  const handleExportPDF = () => {
    if (!segments.length) {
      toast.error('No data to export!');
      return;
    }
    const doc = new jsPDF('landscape');

    doc.setFontSize(16);
    doc.text('Segment-Wise Working Capital Projection', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

    const tableColumn = [
      "Sl.", "Segment Name", "Stock Req. (Rs)", "Credit Days",
      "Credit Avail (Rs)", "Net Paid (Rs)", "Margin %",
      "Margin (Rs)", "WC Finance (Rs)"
    ];

    const tableRows = segments.map((s, i) => {
      const stockReq = parseFloat(s.stock_requirement) || 0;
      const creditPeriod = parseFloat(s.credit_period) || 0;
      const creditAvail = parseFloat(s.credit_available) || 0;
      const netPaid = stockReq - creditAvail;
      const marginPct = s.margin_percentage !== undefined && s.margin_percentage !== '' ? parseFloat(s.margin_percentage) : (parseFloat(globalMarginPerc) || 0);
      const marginAmt = netPaid * (marginPct / 100);
      const wcFinance = netPaid - marginAmt;

      return [
        i + 1,
        s.segment_name,
        fmt(stockReq),
        creditPeriod,
        fmt(creditAvail),
        fmt(netPaid),
        marginPct,
        fmt(marginAmt),
        fmt(wcFinance)
      ];
    });

    tableRows.push([
      '', 'TOTAL', fmt(totalStockReq), '', fmt(totalCreditAvail),
      fmt(totalNetPaid), '', fmt(totalMarginAmt), fmt(totalFinance)
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      footStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 40 },
        2: { halign: 'right' },
        3: { halign: 'center' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'center' },
        7: { halign: 'right' },
        8: { halign: 'right' },
      },
      didParseCell: function (data) {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [220, 237, 200];
        }
      }
    });

    doc.save('Working_Capital_Computations.pdf');
  };

  const innerContent = (
    <>
      <div className={`flex flex-col ${isEmbedded ? '' : 'h-[calc(100vh-112px)]'}`}>
        <div className="shrink-0 mb-4 pb-4 border-b border-slate-800">
          {!isEmbedded && (
            <div className="bg-slate-900/80 border border-slate-700 rounded-2xl p-5 flex flex-wrap items-end gap-4 shadow-xl">
              <div className="flex-1 min-w-[250px]">
                <label className="block text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Select Report</label>
                <Select
                  options={reportsList.map(r => ({ value: r.id, label: r.report_name }))}
                  value={reportId ? { value: reportId, label: reportsList.find(r => r.id === reportId)?.report_name || '' } : null}
                  onChange={selected => setReportId(selected ? selected.value : '')}
                  placeholder="-- Select Project Report --"
                  styles={{
                    ...selectCustomStyles,
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                  menuPortalTarget={document.body}
                  isSearchable
                  isClearable
                  className="text-sm"
                />
              </div>
            </div>
          )}

          {reportId && !loading && (
            <div className="mt-3 bg-slate-900/90 border border-slate-700 rounded-2xl p-4 shadow-2xl shadow-indigo-900/10 mx-auto">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex-1 min-w-[120px]">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-1">Total Stock Req.</p>
                  <p className="text-lg font-mono font-bold text-white">₹{fmt(totalStockReq)}</p>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-1">Total Credit Avail</p>
                  <p className="text-lg font-mono font-bold text-white">₹{fmt(totalCreditAvail)}</p>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-1">Net Paid Stock</p>
                  <p className="text-lg font-mono font-bold text-emerald-400">₹{fmt(totalNetPaid)}</p>
                </div>
                <div className="flex-1 min-w-[120px]">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mb-1">Total Margin</p>
                  <p className="text-lg font-mono font-bold text-amber-400">₹{fmt(totalMarginAmt)}</p>
                </div>
                <div className="flex-1 min-w-[150px] border-l border-slate-700 pl-4">
                  <p className="text-[10px] text-teal-400/80 uppercase tracking-widest font-bold mb-1">Total WC Finance</p>
                  <p className="text-2xl font-mono font-black text-teal-400 drop-shadow-[0_0_8px_rgba(45,212,191,0.4)]">₹{fmt(totalFinance)}</p>
                </div>
              </div>
            </div>
          )}

          {reportId && !loading && (
            <div className="mt-3 p-5 border border-slate-700 bg-slate-800/80 rounded-2xl flex flex-col md:flex-row justify-between items-center gap-4 shadow-lg backdrop-blur-xl">
              <div className="flex items-center gap-4 flex-wrap">
                <h3 className="font-bold text-lg text-white">Segment-Wise Computation</h3>
                <div className="flex items-center gap-2 bg-slate-950 px-4 py-1.5 rounded-xl border border-amber-500/40 shadow-inner">
                  <span className="text-xs font-bold text-amber-300 uppercase tracking-wider">Margin :</span>
                  <input
                    type="number"
                    value={globalMarginPerc}
                    readOnly
                    title="Configured in Setting Percentage menu"
                    className="w-12 bg-transparent text-white font-mono font-bold text-center outline-none cursor-not-allowed opacity-80"
                  />
                  <span className="text-xs text-slate-500">%</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-4 py-1.5 rounded-lg text-xs font-semibold shadow-[0_0_15px_rgba(16,185,129,0.3)] hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] transition-all hover:-translate-y-0.5 border border-emerald-500/30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Excel Export
                </button>
                <button
                  onClick={handleExportPDF}
                  className="flex items-center gap-2 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white px-4 py-1.5 rounded-lg text-xs font-semibold shadow-[0_0_15px_rgba(225,29,72,0.3)] hover:shadow-[0_0_25px_rgba(225,29,72,0.5)] transition-all hover:-translate-y-0.5 border border-rose-500/30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 9h1.5m1.5 0H15m-1.5 4h-3m3 4h-3" /></svg>
                  PDF Export
                </button>
                <button
                  onClick={handleAddRow}
                  className="flex items-center gap-2 bg-indigo-600/20 hover:bg-indigo-500/30 text-indigo-300 px-4 py-1.5 rounded-lg text-xs font-bold shadow-[0_0_15px_rgba(99,102,241,0.2)] hover:shadow-[0_0_20px_rgba(99,102,241,0.4)] transition-all hover:-translate-y-0.5 border border-indigo-500/30 ml-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                  Add New Segment
                </button>
              </div>
            </div>
          )}
        </div>

        {!reportId ? (
          <div className="flex-1 flex flex-col justify-center items-center bg-slate-900/40 border border-slate-800 rounded-2xl p-12 text-center">
            <svg className="w-16 h-16 text-slate-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <h3 className="text-xl font-bold text-white mb-2">No Report Selected</h3>
            <p className="text-slate-400 max-w-md mx-auto">Please select a project report from the dropdown above to view or edit working capital segments.</p>
          </div>
        ) : loading ? (
          <div className="flex-1 flex justify-center items-center">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex-1 min-h-[200px] flex flex-col bg-slate-900/60 border border-slate-700 rounded-3xl overflow-hidden shadow-2xl backdrop-blur-xl">
              <div className="flex-1 overflow-x-auto overflow-y-auto hide-scrollbar" ref={tableContainerRef}>
                <table className="w-full text-left whitespace-nowrap">
                  <thead className="bg-slate-950 border-b border-slate-700 text-xs uppercase tracking-wider text-slate-400 font-semibold sticky top-0 z-20 shadow-md">
                    <tr>
                      <th className="py-3 px-4 w-12 text-center">Sl.</th>
                      <th className="py-3 px-4 min-w-[300px]">Segment Name</th>
                      <th className="py-3 px-4 bg-indigo-950/20 text-indigo-300">Stock Requirement (₹)</th>
                      <th className="py-3 px-4 bg-indigo-950/20 text-indigo-300 w-24">Credit Period (Days)</th>
                      <th className="py-3 px-4 bg-indigo-950/20 text-indigo-300">Credit Available (₹)</th>
                      <th className="py-3 px-4 text-emerald-400 bg-emerald-950/10">Net Paid Stock (₹)</th>
                      <th className="py-3 px-4 bg-amber-950/20 text-amber-300 min-w-[100px] text-center">Margin %</th>
                      <th className="py-3 px-4 text-emerald-400 bg-emerald-950/10">Margin (₹)</th>
                      <th className="py-3 px-4 text-teal-300 bg-teal-950/20 font-bold border-l border-slate-700">WC Finance (₹)</th>
                      <th className="py-3 px-4 w-16 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {segments.map((s, i) => {
                      const stockReq = parseFloat(s.stock_requirement) || 0;
                      const creditAvail = parseFloat(s.credit_available) || 0;
                      const netPaid = stockReq - creditAvail;
                      const marginPerc = s.margin_percentage !== undefined && s.margin_percentage !== '' ? parseFloat(s.margin_percentage) : (parseFloat(globalMarginPerc) || 0);
                      const marginAmt = netPaid * (marginPerc / 100);
                      const wcFinance = netPaid - marginAmt;

                      return (
                        <tr key={s.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="py-2 px-4 text-center text-slate-500 font-mono text-xs">{i + 1}</td>
                          <td className="py-2 px-4">
                            <input type="text" value={s.segment_name} onChange={e => handleChange(i, 'segment_name', e.target.value)} placeholder="Enter Segment Name" className="w-full bg-slate-950/50 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500" />
                          </td>
                          <td className="py-2 px-4 bg-indigo-950/10">
                            <input type="text" value={s.stock_requirement || ''} onChange={e => handleChange(i, 'stock_requirement', e.target.value)} placeholder="0" className="w-full bg-slate-950/50 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-indigo-100 font-mono text-right focus:outline-none focus:border-indigo-500" />
                          </td>
                          <td className="py-2 px-4 bg-indigo-950/10">
                            <input type="text" value={s.credit_period || ''} onChange={e => handleChange(i, 'credit_period', e.target.value)} placeholder="0" className="w-20 mx-auto block bg-slate-950/50 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-indigo-100 font-mono text-center focus:outline-none focus:border-indigo-500" />
                          </td>
                          <td className="py-2 px-4 bg-indigo-950/10">
                            <input type="text" value={s.credit_available || ''} onChange={e => handleChange(i, 'credit_available', e.target.value)} placeholder="0" className="w-full bg-slate-950/50 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-indigo-100 font-mono text-right focus:outline-none focus:border-indigo-500" />
                          </td>
                          <td className="py-2 px-4 bg-emerald-950/5 text-right font-mono text-sm text-emerald-300">
                            {fmt(netPaid)}
                          </td>
                          <td className="py-2 px-4 bg-amber-950/10 text-center font-mono text-sm text-amber-200/80">
                            <div className="flex items-center justify-center gap-1">
                              <input 
                                type="text" 
                                value={s.margin_percentage !== undefined ? s.margin_percentage : globalMarginPerc} 
                                onChange={e => handleChange(i, 'margin_percentage', e.target.value)} 
                                className="w-12 bg-slate-950/50 border border-slate-700 rounded-lg px-2 py-1 text-sm text-amber-100 font-mono text-center focus:outline-none focus:border-amber-500"
                              />
                              <span className="text-amber-500/50">%</span>
                            </div>
                          </td>
                          <td className="py-2 px-4 bg-emerald-950/5 text-right font-mono text-sm text-emerald-300">
                            {fmt(marginAmt)}
                          </td>
                          <td className="py-2 px-4 bg-teal-950/10 text-right font-mono font-bold text-sm text-teal-400 border-l border-slate-700">
                            {fmt(wcFinance)}
                          </td>
                          <td className="py-2 px-4 text-center">
                            <button onClick={() => handleRemoveRow(i, s.id)} className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-lg transition">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot className="sticky bottom-0 z-20 shadow-[0_-4px_15px_rgba(0,0,0,0.5)]">
                    <tr>
                      <td colSpan="2" className="py-4 px-4 font-bold text-right text-emerald-400 uppercase tracking-widest text-xs bg-slate-950 border-t-2 border-slate-700">TOTAL</td>
                      <td className="py-4 px-4 text-right font-black text-emerald-400 font-mono bg-slate-950 border-t-2 border-slate-700 border-r border-slate-800">{fmt(totalStockReq)}</td>
                      <td className="py-4 px-4 bg-slate-950 border-t-2 border-slate-700"></td>
                      <td className="py-4 px-4 text-right font-black text-emerald-400 font-mono bg-slate-950 border-t-2 border-slate-700 border-l border-r border-slate-800">{fmt(totalCreditAvail)}</td>
                      <td className="py-4 px-4 text-right font-black text-emerald-400 font-mono bg-slate-950 border-t-2 border-slate-700 border-r border-slate-800">{fmt(totalNetPaid)}</td>
                      <td className="py-4 px-4 bg-slate-950 border-t-2 border-slate-700"></td>
                      <td className="py-4 px-4 text-right font-black text-emerald-400 font-mono bg-slate-950 border-t-2 border-slate-700 border-l border-r border-slate-800">{fmt(totalMarginAmt)}</td>
                      <td className="py-4 px-4 text-right font-black text-teal-400 font-mono bg-slate-950 border-t-2 border-slate-700 border-l border-slate-800 text-base">{fmt(totalFinance)}</td>
                      <td className="bg-slate-950 border-t-2 border-slate-700"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="p-4 bg-slate-950 border-t border-slate-800 flex justify-between items-center shrink-0">
                <button onClick={handleAddRow} className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 px-4 py-2 rounded-xl text-sm font-bold border border-indigo-500/20 transition flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                  Add New Segment
                </button>
                <button
                  onClick={handleSaveAll}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition shadow-lg shadow-emerald-600/20 disabled:opacity-50 flex items-center gap-2 h-[42px]"
                >
                  {saving ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  Save All
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 max-w-sm w-full shadow-2xl relative">
            <div className="w-16 h-16 rounded-full bg-rose-50-10 border border-rose-500-20 flex items-center justify-center mx-auto mb-4 shadow-[0_0_15px_rgba(244,63,94,0.3)]">
              <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">Delete Segment?</h3>
            <p className="text-slate-400 text-center text-sm mb-6">Are you sure you want to delete this segment permanently? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal({ isOpen: false, index: null, id: null })} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-700 text-slate-300 font-bold hover:bg-slate-800 transition">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold transition shadow-lg shadow-rose-600/20">Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (isEmbedded) {
    return <div className="embedded-working-capital">{innerContent}</div>;
  }

  return (
    <Layout title="Working Capital Computations" subtitle="Segment-wise working capital projection">
      {innerContent}
    </Layout>
  );
};

export default WorkingCapitalMaster;
